/* eslint-disable jsx-a11y/control-has-associated-label */
import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import ProductImage from 'src/components/ui/ProductImage/ProductImage';
import Input from 'src/components/ui/Input/Input';
import styles from './BomLinesOverview.module.scss';

/**
 * A single BOM line as held in the BOM tab's working set.
 * `lineId` is the persisted `BomLine` id; it is `null` for a line that was added
 * in the current edit session and not yet saved to the backend.
 */
export interface BomWorkingLine {
  lineId: number | null;
  product_id: number;
  quantity: number;
  name?: string | null;
  article_number?: string | null;
  barcode?: string | null;
}

interface BomLinesOverviewProps {
  lines: BomWorkingLine[];
  editable?: boolean;
  onQuantityChange?: (productId: number, quantity: number) => void;
  onRemove?: (productId: number) => void;
}

const BomLinesOverview: React.FC<BomLinesOverviewProps> = ({ lines, editable = false, onQuantityChange, onRemove }) => {
  const { t } = useTranslation();

  if (lines.length === 0) {
    return null;
  }

  return (
    <div className={classNames(styles.root, 'mb-16')}>
      <table>
        <thead>
          <tr>
            <th className="typo-text-12 text-faded w-full">{t('general.name')}</th>
            <th className="typo-text-12 text-faded">{t('general.article_number_short')}</th>
            <th className="typo-text-12 text-faded">{t('general.barcode')}</th>
            <th className="typo-text-12 text-faded">{t('general.quantity')}</th>
            {editable && <th className="typo-text-12 text-faded">{t('general.remove')}</th>}
          </tr>
        </thead>
        <tbody>
          {lines.map(line => (
            <tr key={line.product_id} className={styles.product}>
              <td className="flex items-center gap-x-12">
                <ProductImage id={line.product_id} />
                <div>
                  <h4 className="typo-text-14--bold text-full">{line.name}</h4>
                </div>
              </td>
              <td className="typo-text-14 text-full">{line.article_number}</td>
              <td className="typo-text-14 text-full">{line.barcode}</td>
              <td>
                {editable ? (
                  <Input
                    type="number"
                    placeholder={t('general.quantity')}
                    min={1}
                    value={line.quantity}
                    onChange={e =>
                      onQuantityChange?.(line.product_id, e.target.value === '' ? 0 : Number(e.target.value))
                    }
                    aria-label={t('general.quantity')}
                  />
                ) : (
                  <span className="typo-text-14 text-full">{line.quantity}</span>
                )}
              </td>
              {editable && (
                <td>
                  <Button tone={ButtonToneProps.ERROR} icon="delete" onClick={() => onRemove?.(line.product_id)} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BomLinesOverview;
